<script setup>
import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import '@vue-flow/minimap/dist/style.css';

import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import FlowsAPI from 'dashboard/api/flows';

import FlowToolbar from './FlowToolbar.vue';
import FlowBlocksSidebar from './FlowBlocksSidebar.vue';
import FlowCanvas from './FlowCanvas.vue';
import FlowNodeConfigPanel from './FlowNodeConfigPanel.vue';
import FlowJsonDrawer from './FlowJsonDrawer.vue';
import FlowValidationPanel from './FlowValidationPanel.vue';

import schemaToVueFlow from './utils/schemaToVueFlow';
import vueFlowToSchema from './utils/vueFlowToSchema';
import validateCanvas from './utils/flowValidation';
import {
  createCanvasNode,
  createFallbackNode,
  EXAMPLE_SCHEMA,
} from './utils/nodeFactory';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const flowId = computed(() => route.params.flowId);

const nodes = ref([]);
const edges = ref([]);
const flowName = ref('');
const flowStatus = ref('draft');
const flowTriggerType = ref('');
const flowTriggerLabel = ref('');
const flowInboxesSummary = ref('');
const loading = ref(true);
const saving = ref(false);
const dirty = ref(false);
const initialized = ref(false);

const selectedNodeId = ref(null);
const selectedNode = computed(
  () => nodes.value.find(node => node.id === selectedNodeId.value) || null
);

const showJsonDrawer = ref(false);
const showValidation = ref(false);
const validationErrors = ref([]);
const validationWarnings = ref([]);

const canvasRef = ref(null);

const currentSchema = () => vueFlowToSchema(nodes.value, edges.value, null);
const currentSchemaJson = computed(() =>
  JSON.stringify(currentSchema(), null, 2)
);

const markDirty = () => {
  if (initialized.value) dirty.value = true;
};

const loadSchemaIntoCanvas = schema => {
  const converted = schemaToVueFlow(schema, t);
  nodes.value = converted.nodes;
  edges.value = converted.edges;
};

const fetchFlow = async () => {
  loading.value = true;
  try {
    const { data } = await FlowsAPI.show(flowId.value);
    flowName.value = data.name;
    flowStatus.value = data.status;
    flowTriggerType.value = data.trigger_type;
    flowTriggerLabel.value = t(`FLOWS.FORM.TRIGGER.${(data.trigger_type || 'manual').toUpperCase()}`);
    flowInboxesSummary.value = data.applies_to_all_inboxes
      ? t('FLOWS.CARD.ALL_INBOXES')
      : (data.inboxes || []).map(i => i.name).join(', ') || t('FLOWS.CARD.ALL_INBOXES');
    loadSchemaIntoCanvas(data.draft_schema || {});
    setTimeout(() => {
      initialized.value = true;
    }, 500);
  } catch (error) {
    useAlert(t('FLOWS.API.ERROR'));
  } finally {
    loading.value = false;
  }
};

const runLocalValidation = () => {
  const result = validateCanvas(nodes.value, edges.value, t);
  validationErrors.value = result.errors;
  validationWarnings.value = result.warnings;
  return result;
};

const save = async () => {
  saving.value = true;
  try {
    await FlowsAPI.update(flowId.value, {
      flow: { name: flowName.value, draft_schema: currentSchema() },
    });
    dirty.value = false;
    useAlert(t('FLOWS.API.SAVE_SUCCESS'));
    return true;
  } catch (error) {
    useAlert(error?.response?.data?.errors?.join(', ') || t('FLOWS.API.ERROR'));
    return false;
  } finally {
    saving.value = false;
  }
};

const validate = async () => {
  runLocalValidation();
  showValidation.value = true;
  try {
    const { data } = await FlowsAPI.validate(flowId.value, currentSchema());
    (data.errors || []).forEach(message => {
      if (!validationErrors.value.includes(message)) {
        validationErrors.value.push(message);
      }
    });
  } catch (error) {
    // backend validation is best-effort here; activation still enforces it
  }
};

const activate = async () => {
  const result = runLocalValidation();
  if (result.errors.length) {
    showValidation.value = true;
    useAlert(t('FLOWS.BUILDER.TOOLBAR.FIX_ERRORS'));
    return;
  }
  const saved = await save();
  if (!saved) return;
  try {
    await FlowsAPI.activate(flowId.value);
    flowStatus.value = 'active';
    useAlert(t('FLOWS.API.ACTIVATE_SUCCESS'));
  } catch (error) {
    validationErrors.value = error?.response?.data?.errors || [t('FLOWS.API.ERROR')];
    showValidation.value = true;
  }
};

const deactivate = async () => {
  try {
    await FlowsAPI.deactivate(flowId.value);
    flowStatus.value = 'inactive';
    useAlert(t('FLOWS.API.DEACTIVATE_SUCCESS'));
  } catch (error) {
    useAlert(t('FLOWS.API.ERROR'));
  }
};

const addNode = type => {
  const position = { x: 120 + Math.random() * 120, y: 120 + Math.random() * 120 };
  const node =
    type === 'fallback'
      ? createFallbackNode(position, t)
      : createCanvasNode(type, position, t);
  nodes.value.push(node);
  selectedNodeId.value = node.id;
  markDirty();
};

const onDropNode = ({ type, position }) => {
  const node =
    type === 'fallback'
      ? createFallbackNode(position, t)
      : createCanvasNode(type, position, t);
  nodes.value.push(node);
  selectedNodeId.value = node.id;
  markDirty();
};

const onNodeSelected = nodeId => {
  selectedNodeId.value = nodeId;
};

const onUpdateNodeData = ({ nodeId, data }) => {
  nodes.value = nodes.value.map(node =>
    node.id === nodeId ? { ...node, data } : node
  );
  markDirty();
};

const onRenameHandle = ({ nodeId, from, to }) => {
  edges.value = edges.value.map(edge =>
    edge.source === nodeId && edge.sourceHandle === from
      ? { ...edge, sourceHandle: to, id: `e-${nodeId}-${to}-${edge.target}` }
      : edge
  );
};

const onRemoveHandle = ({ nodeId, handle }) => {
  edges.value = edges.value.filter(
    edge => !(edge.source === nodeId && edge.sourceHandle === handle)
  );
};

const onDeleteNode = nodeId => {
  nodes.value = nodes.value.filter(node => node.id !== nodeId);
  edges.value = edges.value.filter(
    edge => edge.source !== nodeId && edge.target !== nodeId
  );
  if (selectedNodeId.value === nodeId) selectedNodeId.value = null;
  markDirty();
};

const insertExample = () => {
  loadSchemaIntoCanvas(JSON.parse(JSON.stringify(EXAMPLE_SCHEMA)));
  selectedNodeId.value = null;
  markDirty();
  setTimeout(() => canvasRef.value?.fitView({ padding: 0.15 }), 100);
};

const applyJson = parsedSchema => {
  loadSchemaIntoCanvas(parsedSchema);
  showJsonDrawer.value = false;
  selectedNodeId.value = null;
  markDirty();
  setTimeout(() => canvasRef.value?.fitView({ padding: 0.15 }), 100);
};

const closeValidation = () => {
  showValidation.value = false;
  validationErrors.value = [];
  validationWarnings.value = [];
};

const goBack = () => router.push({ name: 'flows_list', params: route.params });

onMounted(fetchFlow);
</script>

<template>
  <div class="flex flex-col w-full h-full min-h-0 bg-n-background">
    <FlowToolbar
      :name="flowName"
      :status="flowStatus"
      :dirty="dirty"
      :saving="saving"
      :has-errors="validationErrors.length > 0"
      :trigger-type="flowTriggerType"
      :trigger-label="flowTriggerLabel"
      :inboxes-summary="flowInboxesSummary"
      @back="goBack"
      @settings="goBack"
      @update-name="value => { flowName = value; markDirty(); }"
      @save="save"
      @validate="validate"
      @activate="activate"
      @deactivate="deactivate"
      @show-json="showJsonDrawer = true"
      @insert-example="insertExample"
      @fit-view="canvasRef?.fitView({ padding: 0.15 })"
    />

    <div v-if="loading" class="flex items-center justify-center flex-1 text-n-slate-11">
      {{ $t('FLOWS.LOADING') }}
    </div>

    <div v-else class="relative flex flex-1 min-h-0">
      <FlowBlocksSidebar @add-node="addNode" />
      <div class="relative flex-1 min-w-0">
        <FlowCanvas
          ref="canvasRef"
          v-model:nodes="nodes"
          v-model:edges="edges"
          @node-selected="onNodeSelected"
          @drop-node="onDropNode"
          @changed="markDirty"
        />
        <FlowValidationPanel
          :show="showValidation"
          :errors="validationErrors"
          :warnings="validationWarnings"
          @close="closeValidation"
        />
        <FlowJsonDrawer
          :show="showJsonDrawer"
          :schema-json="currentSchemaJson"
          @close="showJsonDrawer = false"
          @apply="applyJson"
        />
      </div>
      <FlowNodeConfigPanel
        :node="selectedNode"
        @update-data="onUpdateNodeData"
        @rename-handle="onRenameHandle"
        @remove-handle="onRemoveHandle"
        @delete-node="onDeleteNode"
        @close="selectedNodeId = null"
      />
    </div>
  </div>
</template>

<style scoped>
:deep(.vue-flow__minimap) {
  @apply rounded-lg border border-n-weak;
}
</style>
